import { createFileRoute } from '@tanstack/react-router';

import { GroundTruthEditorShell } from '@/features/GroundTruthHub/GroundTruthEditorShell';
import { GroundTruthPreview } from '@/features/GroundTruthHub/GroundTruthPreview';
import { SheetSplitEditor } from '@/features/GroundTruthHub/SheetSplitEditor';
import { isNotFoundError, useGtFileStatus } from '@/features/GroundTruthHub/api';
import { ingestedFileIdParams } from '@/features/GroundTruthHub/routeParams';
import { useWorkbookSheetNames } from '@/features/GroundTruthHub/useWorkbookSheetNames';
import {
    GroundTruthNotFound,
    groundTruthRouteBoundaries,
} from '@/features/GroundTruthHub/GroundTruthNotFound';

/**
 * Exported (rather than kept module-private like most route components) so it
 * can be unit-tested directly with plain props, bypassing `createFileRoute`'s
 * router-context requirement — see `sheets.test.tsx`.
 */
export function SheetSplitRoute({ ingestedFileId }: { ingestedFileId: number }) {
    const {
        data: fileRow,
        isLoading,
        isError,
        error,
    } = useGtFileStatus(ingestedFileId);

    const ext = (fileRow?.fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
    const isExcel = ext === 'xlsx' || ext === 'xls';

    const excelUrl = isExcel ? (fileRow?.presignedUrl ?? null) : null;
    const { sheetNames, onExcelSheetNames } = useWorkbookSheetNames(excelUrl, ingestedFileId);

    if (isError && isNotFoundError(error)) {
        return <GroundTruthNotFound what={`File #${ingestedFileId}`} />;
    }

    return (
        <GroundTruthEditorShell title={`Sheet splits — file #${ingestedFileId}`}>
            {isLoading ? (
                <div
                    className="flex h-full items-center justify-center text-sm text-muted-foreground"
                    data-testid="SheetSplitRoute__loading"
                >
                    Loading…
                </div>
            ) : isError ? (
                <div
                    className="flex h-full items-center justify-center text-sm text-muted-foreground"
                    data-testid="SheetSplitRoute__error"
                >
                    Couldn’t load this file. Reload the page to try again.
                </div>
            ) : (
                // Excel sources have no rendered PDF — `docId={null}` degrades
                // the preview pane to the Excel table (or its placeholder),
                // matching GroundTruthFilePage's `isPdf ? docId : null` for
                // the sheets tab. This route is directly URL-addressable, so
                // `excelPresignedUrl` must also be gated on `isExcel` — the
                // backend now signs presigned URLs for every file with an
                // object key, not just Excel ones, so an ungated pass-through
                // would try to parse a PDF as xlsx.
                <GroundTruthPreview
                    docId={null}
                    excelPresignedUrl={excelUrl}
                    onExcelSheetNames={onExcelSheetNames}
                >
                    <SheetSplitEditor
                        ingestedFileId={ingestedFileId}
                        sheetNames={sheetNames}
                    />
                </GroundTruthPreview>
            )}
        </GroundTruthEditorShell>
    );
}

function SheetSplitRouteComponent() {
    const { ingestedFileId } = Route.useParams();
    return <SheetSplitRoute ingestedFileId={ingestedFileId} />;
}

export const Route = createFileRoute('/ground-truth/$ingestedFileId/sheets')({
    ...groundTruthRouteBoundaries,
    component: SheetSplitRouteComponent,
    params: ingestedFileIdParams,
});
